import { Vec2 } from "miglib";
import { Tile } from "./Tile";
import { Faction, UnitRegistry } from "./types";
import { World } from "./World";
import { GameScreen } from "./GameScreen";
import { positionsInRadius } from "./geometry";

export class ObjGen {

    constructor(
        private main: GameScreen,
        private units: UnitRegistry,
        private worldSize: number,
        private overworld: World,
        private netherworld: World,
        private readonly random: () => number = Math.random,
        private isTutorial: boolean = false
    ) { }

    setup() {
        this.setupOverworld();
        this.setupNetherworld();
    }

    setupOverworld() {

        const extraNr = Math.floor(this.worldSize / 5);

        const toPlace = [
            { number: 2 + extraNr, make: () => this.units.chapel.make({ faction: "neutral" }), world: this.overworld },
            { number: 4 + extraNr, make: () => this.units.castle.make({ faction: "neutral" }), world: this.overworld },
            { number: 2 + extraNr, make: () => this.units.shrine.make({ faction: "neutral" }), world: this.overworld },
            { number: 8 + extraNr, make: () => this.units.house.make({ faction: "neutral" }), world: this.overworld },
            { number: 2 + extraNr, make: () => this.units.tower.make({ faction: "neutral" }), world: this.overworld },
        ]
        const totalToPlace = toPlace.reduce((sum, item) => sum + item.number, 0);

        for (const { x, y } of this.findPositions(this.overworld, totalToPlace, 4)) {
            const next = toPlace[0];
            const obj = next.make();
            this.overworld.set(x, y, obj);
            next.number--;
            if (next.number === 0) toPlace.shift();
        }

    }

    setupNetherworld() {
        const extNr = Math.floor(this.worldSize / 5);

        const distToEdge = Math.floor(this.worldSize * .25);
        const toPlace: { pos: Vec2, faction: Faction }[] = [
            { pos: { x: distToEdge, y: distToEdge }, faction: "player" },
            { pos: { x: this.worldSize - distToEdge, y: distToEdge }, faction: "blue" },
            { pos: { x: distToEdge, y: this.worldSize - distToEdge }, faction: "green" },
            // { pos: { x: worldSize - 20, y: worldSize - 10 }, faction: "green" },
        ]

        for (const place of toPlace) {

            const { faction } = place;
            const pos = this.isTutorial ? place.pos : this.randomLandNear(this.netherworld, place.pos, 5);

            this.netherworld.set(pos.x, pos.y, this.units.seat.make({ faction }));
            for (const pos2 of positionsInRadius(pos.x, pos.y, 4)) {
                this.main.influenceMap.set(pos2.x, pos2.y, faction);
            }
        }

        for (const { x, y } of this.findPositions(this.netherworld, 5 + extNr, 5))
            this.netherworld.set(x, y, this.units.cauldron.make({ faction: "neutral" }));

    }

    private randomLandNear(world: World, base: Vec2, range: number): Vec2 {
        for (let r = range; r <= this.worldSize; r += range) {
            const candidates = positionsInRadius(base.x, base.y, r)
                .filter(p => world.getTile(p.x, p.y)?.type === "land");
            if (candidates.length > 0) return candidates[Math.floor(this.random() * candidates.length)];
        }
        return base;
    }

    findPositions(world: World, count: number, minDist: number): { x: number; y: number }[] {
        return this.findEligiblePositions(world, count, minDist, (x: number, y: number, tile: Tile) => tile.type === "land");
    }

    private findEligiblePositions(
        world: World,
        count: number,
        minDistance: number,
        isEligible: (x: number, y: number, tile: Tile) => boolean
    ): { x: number; y: number }[] {

        const occupied = new Set<string>();
        for (const { x, y } of world) {
            occupied.add(`${x},${y}`);
        }

        const candidates: { x: number; y: number }[] = [];
        for (const { x, y, tile } of world.tilesIterator()) {
            if (occupied.has(`${x},${y}`)) continue;
            if (!isEligible(x, y, tile)) continue;
            candidates.push({ x, y });
        }

        for (let i = candidates.length - 1; i > 0; i--) {
            const j = Math.floor(this.random() * (i + 1));
            [candidates[i], candidates[j]] = [candidates[j], candidates[i]];
        }

        const chosen: { x: number; y: number }[] = [];
        const minDistanceSq = minDistance * minDistance;

        for (const pos of candidates) {
            if (chosen.length >= count) break;

            const tooCloseToChosen = chosen.some(c => {
                const dx = c.x - pos.x;
                const dy = c.y - pos.y;
                return dx * dx + dy * dy < minDistanceSq;
            });

            if (!tooCloseToChosen) {
                chosen.push(pos);
            }
        }

        return chosen;
    }

}